import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { listPendingIpas, reviewIpa } from "services/consentService";

const IpaReviewPage = () => {
  const { isGlobalAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesByInstitution, setNotesByInstitution] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listPendingIpas());
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load pending IPAs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isGlobalAdmin) load();
  }, [isGlobalAdmin, load]);

  const handleReview = async (institutionId, reviewAction) => {
    setErrorMessage("");
    try {
      await reviewIpa({
        institutionId,
        reviewAction,
        reviewNotes: notesByInstitution[institutionId] || "",
      });
      await load();
    } catch (err) {
      setErrorMessage(err?.message || "Review failed.");
    }
  };

  if (!isGlobalAdmin) {
    return <Alert severity="warning">Global admin access required.</Alert>;
  }

  if (loading) return <Loading page />;

  return (
    <Stack spacing={3}>
      <DashboardPageHeader
        title="IPA review inbox"
        subtitle="Platform owner review for Institutional Participation Agreements."
      />
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {!rows.length ? (
        <Typography color="text.secondary">No submissions pending review.</Typography>
      ) : (
        rows.map((row) => (
          <Paper key={row.id || row.institutionId} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1">{row.institutionId}</Typography>
            <Typography variant="body2" color="text.secondary">
              Signatory: {row.boardResolutionSignatory || "—"}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              label="Review notes"
              value={notesByInstitution[row.institutionId] || ""}
              onChange={(event) =>
                setNotesByInstitution((prev) => ({
                  ...prev,
                  [row.institutionId]: event.target.value,
                }))
              }
            />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => handleReview(row.institutionId, "approved")}>
                Approve
              </Button>
              <Button variant="outlined" onClick={() => handleReview(row.institutionId, "clarification-requested")}>
                Request clarification
              </Button>
              <Button color="error" onClick={() => handleReview(row.institutionId, "rejected")}>
                Reject
              </Button>
            </Stack>
          </Paper>
        ))
      )}
    </Stack>
  );
};

export default IpaReviewPage;
